import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
// import moment from 'moment';
import buildExportQuery from "./queryies/_export_all";
import { saveAs } from "file-saver";

interface WorkPlanBoardState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  lastMeta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  documents: any[];
}

const state: WorkPlanBoardState = {
  loading: false,
  loaded: false,
  fatalError: false,

  lastMeta: {
    page: 0,
    pageSize: 0,
    totalCount: 0,
    totalPages: 0
  },

  documents: []
};

export const ITEMS_LIST = "GET_CLIENT_LIST";

export const actions: ActionTree<WorkPlanBoardState, RootState> = {
  loadData({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      const columns: any = {
        id: "cl.id",
        name: "cl.name",
        category_income: "cl.category_income",
        category_profit: "cl.category_profit",
        manager_id: "empl.id",
        manager_name: "empl.name",
        city_id: "city.id",
        city_name: "city.name",
        client_type: "ctype.name",
        payment_type_id: "ptype.id",
        payment_type_name: "ptype.name",
        archived_at: "cl.archived_at",
        expected_delivery_date: "deals.expected_delivery_date"
      };

      const filter: any[] = [];

      if (query.name) {
        filter.push({ $ilike: ["cl.name", { $: `%${query.name}%` }] });
      }

      if (query.cities) {
        const $cities = Array.isArray(query.cities)
          ? query.cities
          : [query.cities];
        if ($cities.length > 0) {
          filter.push({ $in: ["cl.city_id", { $: $cities }] });
        }
      }

      if (query.paymentTypes) {
        const $paymentTypes = Array.isArray(query.paymentTypes)
          ? query.paymentTypes
          : [query.paymentTypes];
        if ($paymentTypes.length > 0) {
          filter.push({ $in: ["cl.payment_type_id", { $: $paymentTypes }] });
        }
      }

      if (query.managers) {
        const $managers = Array.isArray(query.managers)
          ? query.managers
          : [query.managers];
        if ($managers.length > 0) {
          filter.push({ $in: ["cl.manager_id", { $: $managers }] });
        }
      }

      if (query.clientTypes) {
        const $clientTypes = Array.isArray(query.clientTypes)
          ? query.clientTypes
          : [query.clientTypes];
        if ($clientTypes.length > 0) {
          filter.push({ $in: ["cl.type_id", { $: $clientTypes }] });
        }
      }

      if (query.deliveryZoneGroups) {
        const $deliveryZoneGroups = Array.isArray(query.deliveryZoneGroups)
          ? query.deliveryZoneGroups
          : [query.deliveryZoneGroups];
        if ($deliveryZoneGroups.length > 0) {
          filter.push({
            $in: ["dz.delivery_zone_group_id", { $: $deliveryZoneGroups }]
          });
        }
      }

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action({
            columns: columns,
            from: "s:clients",
            from_as: "cl",
            joins: [
              {
                join: "s:employees",
                type: "left",
                as: "empl",
                on: {
                  $eq: ["cl.manager_id", "empl.id"]
                }
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["cl.city_id", "city.id"]
                }
              },
              {
                join: "s:payment_types",
                type: "left",
                as: "ptype",
                on: {
                  $eq: ["cl.payment_type_id", "ptype.id"]
                }
              },
              {
                join: "s:client_types",
                type: "left",
                as: "ctype",
                on: {
                  $eq: ["cl.type_id", "ctype.id"]
                }
              },
              {
                join: "s:counterparties",
                type: "left",
                as: "counterparty",
                on: {
                  $eq: ["cl.id", "counterparty.client_id"]
                }
              },
              {
                join: "s:delivery_points",
                type: "left",
                as: "dp",
                on: {
                  $eq: ["cl.id", "dp.client_id"]
                }
              },
              {
                join: "s:delivery_points_delivery_zones",
                type: "left",
                as: "dpdz",
                on: {
                  $eq: ["dpdz.delivery_point_id", "dp.id"]
                }
              },
              {
                join: "s:delivery_zones",
                type: "left",
                as: "dz",
                on: {
                  $eq: ["dpdz.delivery_zone_id", "dz.id"]
                }
              },
              {
                join: {
                  columns: {
                    client_id: "d.client_id",
                    expected_delivery_date: { $max: "d.expected_delivery_date" }
                  },
                  from: "s:deals",
                  from_as: "d",
                  joins: [],
                  groups: ["d.client_id"]
                },
                type: "left",
                as: "deals",
                on: { $eq: ["deals.client_id", "cl.id"] }
              }
            ],
            groups: [
              "cl.id",
              "cl.name",
              "cl.category_income",
              "cl.category_profit",
              "empl.id",
              "empl.name",
              "city.id",
              "city.name",
              "ptype.id",
              "ptype.name",
              "ctype.name",
              "deals.expected_delivery_date"
            ],
            page_size: 100,
            ...{ page: parseInt(query.page) || 1 },
            ...{
              order: [
                { $desc: ["cl.archived_at"] },
                query.sortDirection == "desc"
                  ? { $desc: [columns[query.sortField || "name"]] }
                  : { $asc: [columns[query.sortField || "name"]] }
              ]
            },
            ...(filter.length > 1
              ? {
                  where: {
                    $and: filter
                  }
                }
              : {
                  where: filter[0]
                })
          })
      ]).then((result: ServerResult) => {
        commit("SET_ITEMS_LIST", result.getOperation(ITEMS_LIST).getContent());
        commit("LOADED_SUCCESS");

        resolve();
      });
    });
  },

  exportData({ rootState }) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.export")
          .action(buildExportQuery())
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  }
};

export const mutations: MutationTree<WorkPlanBoardState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_ITEMS_LIST(state, data) {
    state.documents = data.documents;

    state.lastMeta = {
      page: data.meta.page,
      pageSize: data.meta.page_size,
      totalCount: data.meta.total_count,
      totalPages: data.meta.total_pages
    };
  }
};

const getters = {};

export const STORE_KEY = "$_client_list";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
